import React, { useEffect, useCallback, FC, CSSProperties } from "react";
import { toggleMaximise, initializeChat, updateChildFrameState, endChat, closeIntroScreen, minimiseChat, maximiseChat, toggleVoiceInput } from "./chatbotSlice";
import styles from "./Chatbot.module.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { sendPostMessageToChild, updateParentStyle } from "../../utils/connector";
import { VOICE_ICON_HEIGHT, VOICE_ICON_WIDTH, VOICE_INPUT_HEIGHT, VOICE_INPUT_WIDTH } from "../../utils/constants";
import { updateJevaMode } from "../chatConfig/chatConfigSlice";

const Chatbot: FC = () => {
  const dispatch = useAppDispatch();

  const { isMaximised, isInitialized, isMobile, isVoiceInputVisible } = useAppSelector((state) => state.chatbot);
  const chatIconStyle = useAppSelector((state) => state.chatConfig.chatConfig!.config.queue1.chatIconStyle);
  const { chatMode } = useAppSelector((state) => state.chatConfig);
  const { position } = useAppSelector((state) => state.chatConfig.chatConfig!.config);

  const showChatWindow = () => {
    if (!isInitialized) {
      dispatch(initializeChat());
    }
    dispatch(toggleMaximise());
  };

  useEffect(() => {
    if (chatMode === "VOICE") {
      dispatch(closeIntroScreen());
      dispatch(initializeChat());
    }
  }, [chatMode, dispatch]);

  const postMessageHandler = useCallback(
    (e: MessageEvent) => {
      const { data } = e;
      if (data.messageType) {
        if (data.messageType === "CHILD_FRAME_READY") {
          dispatch(updateChildFrameState());
        } else if (data.messageType === "END_CHAT") {
          dispatch(endChat(chatMode));
        } else if (data.messageType === "MINIMISE_CHAT") {
          dispatch(minimiseChat());
        } else if (data.messageType === "MAXIMISE_CHAT") {
          dispatch(maximiseChat());
        } else if (data.messageType === "TOGGLE_VOICE_INPUT") {
          dispatch(toggleVoiceInput(data.payload?.value));
        } else if (data.messageType === "JEVA_MODE_UPDATE") {
          if (["", "conversation", "action", "livechat", "video_call"].includes(data.payload)) {
            dispatch(updateJevaMode(data.payload));
          }
        }
      }
    },
    [dispatch, chatMode]
  );

  useEffect(() => {
    window.addEventListener("message", postMessageHandler);
    return () => {
      window.removeEventListener("message", postMessageHandler);
    };
  }, [postMessageHandler]);

  useEffect(() => {
    updateParentStyle({
      display: "table-cell",
    });
  }, []);

  useEffect(() => {
    (position || "").split("-").forEach((p: any) => {
      updateParentStyle({
        [p]: isMobile ? "0px" : "33px",
      });
    });
  }, [position, isMobile]);

  useEffect(() => {
    const chatWindow = document.getElementById("appChatWindow");
    sendPostMessageToChild({
      messageType: isMaximised ? "PARENT_MAXIMISED" : "PARENT_MINIMISED",
      payload: {},
    });
    if (chatWindow) {
      chatWindow.style.setProperty("display", isMaximised ? "block" : "none", "important");
    }
    if (isMaximised) {
      document.body.style.setProperty("background-color", "#fff");
    } else {
      document.body.style.removeProperty("background-color");
    }
  }, [isMaximised]);

  useEffect(() => {
    if (isMaximised) {
      updateParentStyle({
        height: isMobile ? "100vh" : "602px",
        width: isMobile ? "100vw" : "400px",
        boxShadow: "rgba(0, 0, 0, 0.5) 0px 0px 16px 3px",
        WebkitBoxShadow: "rgba(0, 0, 0, 0.5) 0px 0px 16px 3px",
        borderRadius: "10px",
        appearance: "none",
        WebkitAppearance: "none",
      });
    } else {
      let height, width;

      if (chatMode === "VOICE") {
        if (isVoiceInputVisible) {
          height = VOICE_INPUT_HEIGHT;
          width = VOICE_INPUT_WIDTH;
        } else {
          height = VOICE_ICON_HEIGHT;
          width = VOICE_ICON_WIDTH;
        }
      } else {
        if (chatIconStyle.iconType === "image") {
          height = `${chatIconStyle.imageStyle.height || 70}px`;
          width = `${chatIconStyle.imageStyle.width || 70}px`;
        } else {
          height = `${chatIconStyle.buttonStyle.height || 70}px`;
          width = `${chatIconStyle.buttonStyle.width || 70}px`;
        }
      }

      updateParentStyle({
        height,
        width,
        boxShadow: "unset",
        WebkitBoxShadow: "unset",
        borderRadius: "unset",
        appearance: "unset",
        WebkitAppearance: "unset",
      });
    }
  }, [isMaximised, isMobile, chatIconStyle, chatMode, isVoiceInputVisible]);

  const style: { appChatIcon: CSSProperties } = {
    appChatIcon: {},
  };
  if (chatIconStyle.iconType === "image") {
    const height = chatIconStyle.imageStyle.height || 70;
    const width = chatIconStyle.imageStyle.width || 70;
    style.appChatIcon = {
      height: `${height}px`,
      width: `${width}px`,
      borderRadius: `${chatIconStyle.imageStyle.radiusInPx}px`,
      backgroundColor: `#${chatIconStyle.imageStyle.iconBgColor}`,
      boxShadow: chatIconStyle.imageStyle.shadowFlag ? `#${chatIconStyle.imageStyle.shadowColor} 1px 1px 10px` : "none",
    };
    if (chatIconStyle.imageStyle.borderFlag) {
      style.appChatIcon.border = `solid #${chatIconStyle.imageStyle.borderColor || "000"} 1px`;
    }
  } else if (chatIconStyle.iconType === "button") {
    const height = chatIconStyle.buttonStyle.height || 70;
    const width = chatIconStyle.buttonStyle.width || 70;
    style.appChatIcon = {
      height: `${height}px`,
      width: `${width}px`,
      borderRadius: `${chatIconStyle.buttonStyle.radius}%`,
      backgroundColor: `#${chatIconStyle.buttonStyle.bgColor}`,
      boxShadow: chatIconStyle.buttonStyle.shadowFlag ? `#${chatIconStyle.buttonStyle.shadowColor} 1px 1px 10px` : "none",
    };
    if (chatIconStyle.buttonStyle.borderFlag) {
      style.appChatIcon.border = `solid #${chatIconStyle.buttonStyle.borderColor || "000"} 1px`;
    }
  }

  const containerClasses = `${styles.appChatIcon} ${isMaximised || chatMode === "VOICE" ? styles.appChatIconHidden : ""}`;

  return (
    <>
      <div id="appChatIcon" className={containerClasses} style={style.appChatIcon} onClick={showChatWindow}>
        {chatIconStyle.iconType === "image" ? (
          <img src={chatIconStyle.imageStyle.iconImg} alt="chat-icon" />
        ) : (
          <label
            style={{
              color: `#${chatIconStyle.buttonStyle.labelBgColor}`,
              fontSize: chatIconStyle.buttonStyle.labelSize,
            }}
          >
            {chatIconStyle.buttonStyle.allCaps ? (chatIconStyle.buttonStyle.labelText || "").toUpperCase() : chatIconStyle.buttonStyle.labelText}
          </label>
        )}
      </div>
    </>
  );
};

export default Chatbot;
